import React from "react";
import {useMutation} from "react-query";
import Alert from "react-s-alert";
import {ErrorResponseDto} from "@reside/reside-api-admission";

import {
  AdmissionStatus,
  AdmissionsService,
  isMissingMandatoryChecklistItemException,
} from "../../services/AdmissionsService";
import {ArchiveModal} from "./atoms/ArchiveModal";
import {ModalName, useDashboardModalContext} from "./DashboardModalContext";
import {useDashboardAdmissionsContext} from "./DashboardAdmissionsContext";
import {getAdmissionResidentName} from "./helper";
import {getFormValidationErrors} from "../../utils/formUtils";
import {getWorkflowLabel} from "../start-workflow/workflowMapping";

export const DashboardArchiveModal = ({
  onSuccess,
}: {
  onSuccess?: () => void;
} = {}) => {
  const {refreshDashboard, setAdmissionChecklistErrors} =
    useDashboardAdmissionsContext();
  const {admission, isModalOpened, toggleModal} = useDashboardModalContext(
    ModalName.ARCHIVE,
  );

  const admissionId = admission?.id;

  const {mutate, isLoading} = useMutation(
    (admissionId: string) => AdmissionsService.archive(admissionId),
    {
      onSuccess: archived => {
        Alert.success(
          `${getWorkflowLabel(
            admission?.workflowType,
          )} was successfully archived.`,
        );
        const status = archived.data?.status ?? AdmissionStatus.ARCHIVED;
        if (admissionId && status) {
          refreshDashboard(admissionId, status);
        }
        onSuccess?.();
      },
      onError: async (resp: any) => {
        const error: ErrorResponseDto = await resp.json();

        if (isMissingMandatoryChecklistItemException(error)) {
          setAdmissionChecklistErrors(
            admissionId,
            getFormValidationErrors(error.validationErrors),
          );

          const [firstError] = error.validationErrors;

          Alert.error(firstError.message);
        } else {
          Alert.error(
            `Unable to archive the ${getWorkflowLabel(
              admission?.workflowType,
            )}`,
          );
        }
      },
      onSettled: () => toggleModal(),
    },
  );

  return (
    <ArchiveModal
      residentName={admission ? getAdmissionResidentName(admission) : ""}
      isOpen={isModalOpened}
      isLoading={isLoading}
      onRequestClose={() => toggleModal()}
      onArchive={() => mutate(admissionId)}
      workflowType={admission?.workflowType}
    />
  );
};
